// ────────────────────────────────────────────────────────────────────────
// DressShirtsQuestionnaire — Iter 170.
// Parallel to CasualShirtsQuestionnaire but focused on FORMAL dress shirts.
// The tailor picks:
//   1. Collar / silhouette style (spread, semi-spread, cutaway, point,
//      button-down, wing, tab, club, band)
//   2. Fabric weave (Poplin, Twill, Oxford, End-on-End, Royal Oxford,
//      Herringbone, Pinpoint, Broadcloth)
//   3. Colour (12 dress-shirt appropriate hues)
//   4. Pattern (Solid, Pinstripe, Bengal Stripe, University Stripe,
//      Gingham, Tattersall, Windowpane, Graph Check, Micro Dot,
//      Micro Check)
//   5. Trouser Colour + Trouser Restructure (pleats/cuffs/waistband/belt)
//   6. Shoes (style + colour)
//
// The submit callback returns a combo object shaped like SHIRT_SUBCATEGORIES
// entries so Dashboard drops it into `generateComboLooks` with category
// `shirts_dress_shirts`. Same UI shell as Casual Shirts + Sweaters so the
// tailor sees an identical popout shape across every shirt type.
// ────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import { PANT_COLORS as _PANT_COLORS_RAW } from './CustomizeModal';
import TrouserRestructureBlock, {
  buildTrouserStyleDescription,
  buildTrouserStyleTokens,
} from './TrouserRestructureBlock';

// Trouser palette normalised to { id, hex, label }.
const PANT_COLOURS = _PANT_COLORS_RAW.map(p => ({ id: p.name, hex: p.value, label: p.name }));

// ── Collar / silhouette styles ─────────────────────────────────────
const STYLES = [
  { id: 'spread',        label: 'Spread Collar',       desc: 'The classic business collar — pairs with any tie knot' },
  { id: 'semi_spread',   label: 'Semi-Spread Collar',  desc: 'Moderate opening — the safest all-purpose choice' },
  { id: 'point',         label: 'Point Collar',        desc: 'Narrow closed collar — trim, traditional' },
  { id: 'cutaway',       label: 'Cutaway Collar',      desc: 'Extra-wide opening — favours big knots and open-collar looks' },
  { id: 'button_down',   label: 'Button-Down Collar',  desc: 'Oxford / Ivy staple — button-secured collar tips' },
  { id: 'club',          label: 'Club Collar',         desc: 'Rounded collar edges — vintage editorial' },
  { id: 'tab',           label: 'Tab Collar',          desc: 'Hidden tab under the tie knot — refined boardroom' },
  { id: 'wing',          label: 'Wing Collar',         desc: 'Formal evening — for tuxedo shirts' },
  { id: 'band',          label: 'Band Collar',         desc: 'No collar band — modern minimalist' },
];

// ── Fabric weaves ──────────────────────────────────────────────────
// Each weave has a Gemini-facing prompt phrase that describes the exact
// surface texture the model should render.
const WEAVES = [
  { id: 'poplin',        label: 'Poplin',        prompt: 'plain-weave POPLIN cotton (smooth, crisp, no visible texture — the classic business shirt fabric)' },
  { id: 'twill',         label: 'Twill',         prompt: 'diagonal-weave TWILL cotton (subtle diagonal lines visible in the weave, soft drape, gentle sheen)' },
  { id: 'oxford',        label: 'Oxford',        prompt: 'OXFORD-cloth cotton (visibly textured basketweave, mildly heavier hand, casual-to-business finish)' },
  { id: 'end_on_end',    label: 'End-on-End',    prompt: 'END-ON-END cotton (alternating white and colour warp threads produce a subtle heather cast at arm\'s length)' },
  { id: 'royal_oxford',  label: 'Royal Oxford',  prompt: 'ROYAL OXFORD cotton (fine luxurious basketweave, subtle sheen, distinctly finer than everyday oxford — luxury dress-shirt finish)' },
  { id: 'herringbone',   label: 'Herringbone',   prompt: 'HERRINGBONE cotton (visible V-shaped diagonal weave pattern in tonal white/colour warp, refined formal finish)' },
  { id: 'pinpoint',      label: 'Pinpoint',      prompt: 'PINPOINT cotton (tiny even pin-dot surface texture — between poplin and oxford in weight, refined business finish)' },
  { id: 'broadcloth',    label: 'Broadcloth',    prompt: 'BROADCLOTH cotton (extremely smooth, silky, subtle sheen — highest-formality plain finish)' },
];

// ── Colours (dress-shirt appropriate) ─────────────────────────────
// Kept intentionally restrained — dress shirts are worn in business and
// formal contexts so the palette leans on classic whites, blues, subtle
// tones, and a few refined statement hues. Editorial magazine reference.
const SHIRT_COLOURS = [
  { id: 'White',         label: 'White',         hex: '#FFFFFF' },
  { id: 'Light Blue',    label: 'Light Blue',    hex: '#BCD4E6' },
  { id: 'Sky Blue',      label: 'Sky Blue',      hex: '#9AB9D9' },
  { id: 'French Blue',   label: 'French Blue',   hex: '#6A8FB2' },
  { id: 'Pale Pink',     label: 'Pale Pink',     hex: '#F5D7D9' },
  { id: 'Lavender',      label: 'Lavender',      hex: '#DCD0EC' },
  { id: 'Cream',         label: 'Cream',         hex: '#F4ECD8' },
  { id: 'Ecru',          label: 'Ecru',          hex: '#F0EAD6' },
  { id: 'Light Grey',    label: 'Light Grey',    hex: '#D6D6D8' },
  { id: 'Sage',          label: 'Sage',          hex: '#B5C8B0' },
  { id: 'Charcoal',      label: 'Charcoal',      hex: '#3E3E42' },
  { id: 'Black',         label: 'Black',         hex: '#0B0B0B' },
];

// ── Patterns ───────────────────────────────────────────────────────
// Each pattern has a rich Gemini-facing prompt describing exact scale
// and layout so a "pinstripe" doesn't blur into "bengal stripe" in the
// render. Colour placeholders {colour}/{colourHex} are filled in later.
const PATTERNS = [
  {
    id: 'solid',            label: 'Solid',
    desc: 'Clean single colour — no pattern',
    prompt: ({ colour }) => `solid ${colour.toLowerCase()} (uniform colour across the entire shirt body, sleeves and collar, NO pattern, NO stripes, NO checks)`,
  },
  {
    id: 'pinstripe',        label: 'Pinstripe',
    desc: 'Fine 2-3 mm vertical stripes on white',
    prompt: ({ colour }) => `white background with fine ${colour.toLowerCase()} PINSTRIPES (crisp vertical stripes ~2-3 mm apart, clean parallel lines from shoulder to hem, classic banker pattern)`,
  },
  {
    id: 'bengal_stripe',    label: 'Bengal Stripe',
    desc: 'Bold 6-8 mm alternating stripes',
    prompt: ({ colour }) => `BENGAL STRIPE ${colour.toLowerCase()} and white cotton — alternating vertical stripes ~6-8 mm wide (bolder than pinstripe, evenly spaced, editorial business shirt reference)`,
  },
  {
    id: 'university_stripe', label: 'University Stripe',
    desc: 'Narrow 3-4 mm oxford stripes',
    prompt: ({ colour }) => `UNIVERSITY STRIPE ${colour.toLowerCase()} and white oxford-cloth pattern (narrow ~3-4 mm vertical stripes, Ivy League / Brooks Brothers heritage reference)`,
  },
  {
    id: 'gingham',          label: 'Gingham',
    desc: 'Small even checks on white',
    prompt: ({ colour }) => `GINGHAM check ${colour.toLowerCase()} and white — evenly spaced small checks (~5-6 mm squares in a perfect grid, classic sport-shirt / country-club pattern)`,
  },
  {
    id: 'micro_check',      label: 'Micro Check',
    desc: 'Tiny 2 mm micro checks',
    prompt: ({ colour }) => `MICRO CHECK ${colour.toLowerCase()} on white — very fine ~2 mm checks in a subtle grid, editorial dress-shirt reference (finer + more refined than gingham)`,
  },
  {
    id: 'tattersall',       label: 'Tattersall',
    desc: 'Fine intersecting lines on white',
    prompt: ({ colour }) => `TATTERSALL check pattern (fine intersecting ${colour.toLowerCase()} and navy lines forming small uniform squares ~8-10 mm across, refined countryside / editorial reference)`,
  },
  {
    id: 'windowpane',       label: 'Windowpane',
    desc: 'Large open squares in thin lines',
    prompt: ({ colour }) => `WINDOWPANE CHECK ${colour.toLowerCase()} lines on white (large widely-spaced 3-4 cm open squares outlined in thin single lines, refined editorial pattern)`,
  },
  {
    id: 'graph_check',      label: 'Graph Check',
    desc: 'Even small grid squares',
    prompt: ({ colour }) => `GRAPH CHECK ${colour.toLowerCase()} on white — small even grid squares (~4-5 mm), clean architectural business pattern`,
  },
  {
    id: 'micro_dot',        label: 'Micro Dot',
    desc: 'Tiny tonal dots — refined',
    prompt: ({ colour }) => `MICRO DOT pattern (tiny ~1 mm ${colour.toLowerCase()} dots on white background in a subtle regular grid, editorial dress-shirt reference)`,
  },
];

// ── Shoe catalogue (matches CFQ / Casual Shirts / Sweater) ──────────
const SHOE_STYLES = [
  { id: 'oxford',       label: 'Oxford' },
  { id: 'derby',        label: 'Derby' },
  { id: 'loafer',       label: 'Loafer' },
  { id: 'monk_strap',   label: 'Monk-Strap' },
  { id: 'chelsea_boot', label: 'Chelsea Boot' },
  { id: 'cap_toe',      label: 'Cap-Toe Oxford' },
];

const SHOE_COLOURS = [
  { id: 'Black',        label: 'Black',        hex: '#0A0A0C' },
  { id: 'Dark Brown',   label: 'Dark Brown',   hex: '#3A2418' },
  { id: 'Chocolate',    label: 'Chocolate',    hex: '#503020' },
  { id: 'Cognac',       label: 'Cognac',       hex: '#9B5E2E' },
  { id: 'Tan',          label: 'Tan',          hex: '#C19A6B' },
  { id: 'Oxblood',      label: 'Oxblood',      hex: '#582430' },
];

// Shared visual primitives matching Casual Shirts + Sweater popouts.
const _Section = ({ title, children, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <h4 className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">
      {title}
    </h4>
    {children}
  </div>
);

const _Chip = ({ selected, onClick, hex, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 border text-[11px] uppercase tracking-wider transition-all ${selected
      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]'
      : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'}`}
    data-testid={testid}
  >
    {hex && <span className="w-4 h-4 border border-[#27272A]" style={{ backgroundColor: hex }} />}
    <span>{label}</span>
  </button>
);

export default function DressShirtsQuestionnaire({
  open, onClose, onSubmit,
  generating = false,
  hasClientImage = true,
}) {
  const [styleId,    setStyleId]    = useState(STYLES[0].id);
  const [weaveId,    setWeaveId]    = useState(WEAVES[0].id);
  const [shirtId,    setShirtId]    = useState(SHIRT_COLOURS[0].id);
  const [patternId,  setPatternId]  = useState(PATTERNS[0].id);
  const [pantId,     setPantId]     = useState(PANT_COLOURS.find(p => p.id === 'Charcoal')?.id || PANT_COLOURS[0]?.id || 'Charcoal');
  const [shoeStyle,  setShoeStyle]  = useState('oxford');
  const [shoeColour, setShoeColour] = useState('Black');
  // Iter 169 — shared trouser restructure state.
  const [trPleats,     setTrPleats]     = useState(null);
  const [trCuffs,      setTrCuffs]      = useState(false);
  const [trWaist,      setTrWaist]      = useState(null);
  const [trBeltColor,  setTrBeltColor]  = useState(null);

  const handleSubmit = () => {
    const style   = STYLES.find(s => s.id === styleId) || STYLES[0];
    const weave   = WEAVES.find(w => w.id === weaveId) || WEAVES[0];
    const shirt   = SHIRT_COLOURS.find(c => c.id === shirtId) || SHIRT_COLOURS[0];
    const pattern = PATTERNS.find(p => p.id === patternId) || PATTERNS[0];
    const pant    = PANT_COLOURS.find(c => c.id === pantId) || { id: 'Charcoal', hex: '#36454F', label: 'Charcoal' };
    const shoe    = SHOE_STYLES.find(s => s.id === shoeStyle) || SHOE_STYLES[0];
    const shoeCol = SHOE_COLOURS.find(c => c.id === shoeColour) || SHOE_COLOURS[0];

    // Feature 3 (Feb 2026, iter 170) — assemble a rich fabric_note that
    // stacks: pattern (with colour) + weave + collar style + cuffs +
    // trouser + shoes. Everything TUCKED IN, NO jacket layered on top,
    // and NO black borders. Belt/pleats/cuffs description appended via
    // the shared helper so all three shirt popouts share vocabulary.
    const patternPhrase = pattern.prompt({ colour: shirt.label, colourHex: shirt.hex });
    const collarPhrase  = style.label.toLowerCase();

    const fabric_note = (
      `refined ${weave.prompt} LONG-SLEEVE dress shirt — ${patternPhrase}, `
      + `worn with a ${collarPhrase} and single-button barrel cuffs, `
      + `TUCKED INTO ${pant.label.toLowerCase()} dress trousers (${pant.hex}) with a leather belt, `
      + `${shoeCol.label.toLowerCase()} ${shoe.label.toLowerCase()} shoes, `
      + `NO jacket, NO layer over the shirt. `
      + `Smart-business editorial magazine aesthetic. `
      + `Long sleeves only — never short, never untucked. `
      + `The shirt is the ONLY top-half garment: absolutely NO vest, NO waistcoat, NO gilet, NO cardigan, NO sweater, NO overshirt, NO jacket, NO blazer.`
      + buildTrouserStyleDescription({ trPleats, trCuffs, trWaist, trBeltColor })
    );

    onSubmit?.({
      combo: {
        // Include colour + pattern + collar in the garment name so
        // multiple dress-shirt renders don't collide on dedup.
        garment: `${shirt.label} ${pattern.label} ${style.label}`,
        garment_id: 'dress_shirt',
        style_id: style.id,
        color: shirt.label,
        suit: shirt.hex,
        pattern: pattern.id,
        shirt: shirt.label,
        pant: pant.hex,
        shoe: shoe.id,
        shoeColor: shoeCol.label,
        label: `${shirt.label} ${pattern.label} — ${style.label}`,
        fabric_note,
        // Iter 233 (Feb 2026) — send the composite `trouser_style`
        // token so the backend structural-only lock enforces the
        // waistband choice (side_tabs REPLACES belt loops).
        trouser_style: buildTrouserStyleTokens({ trPleats, trCuffs, trWaist, trBeltColor }) || undefined,
      },
      category: 'shirts_dress_shirts',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A0C] border-[#27272A] rounded-none"
        data-testid="dress-shirts-questionnaire-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Dress Shirt Builder
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA]">
            Pick a collar, fabric weave, colour and pattern — we&apos;ll render one editorial dress-shirt look with your client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* ── Collar / silhouette style ────────────────────── */}
          <_Section title="Collar Style" testid="dress-shirts-style-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STYLES.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyleId(s.id)}
                  className={`text-left px-3 py-3 border transition-all ${styleId === s.id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#3F3F46]'}`}
                  data-testid={`dress-shirts-style-${s.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{s.label}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5">{s.desc}</p>
                </button>
              ))}
            </div>
          </_Section>

          {/* ── Fabric weave ──────────────────────────────────── */}
          <_Section title="Fabric Weave" testid="dress-shirts-weave-section">
            <div className="flex flex-wrap gap-2">
              {WEAVES.map(w => (
                <_Chip
                  key={w.id}
                  selected={weaveId === w.id}
                  onClick={() => setWeaveId(w.id)}
                  label={w.label}
                  testid={`dress-shirts-weave-${w.id}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Shirt colour ─────────────────────────────────── */}
          <_Section title="Shirt Colour" testid="dress-shirts-color-section">
            <div className="flex flex-wrap gap-2">
              {SHIRT_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={shirtId === c.id}
                  onClick={() => setShirtId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`dress-shirts-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Pattern ──────────────────────────────────────── */}
          <_Section title="Pattern" testid="dress-shirts-pattern-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PATTERNS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPatternId(p.id)}
                  className={`text-left px-3 py-3 border transition-all ${patternId === p.id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#3F3F46]'}`}
                  data-testid={`dress-shirts-pattern-${p.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{p.label}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5">{p.desc}</p>
                </button>
              ))}
            </div>
          </_Section>

          {/* ── Pant colour ──────────────────────────────────── */}
          <_Section title="Trouser Colour" testid="dress-shirts-pant-section">
            <div className="flex flex-wrap gap-2">
              {PANT_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={pantId === c.id}
                  onClick={() => setPantId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`dress-shirts-pant-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Trouser Restructure (shared block) ───────────── */}
          <_Section title="Trouser Restructure" testid="dress-shirts-trouser-restructure-section">
            <TrouserRestructureBlock
              trPleats={trPleats}       setTrPleats={setTrPleats}
              trCuffs={trCuffs}         setTrCuffs={setTrCuffs}
              trWaist={trWaist}         setTrWaist={setTrWaist}
              trBeltColor={trBeltColor} setTrBeltColor={setTrBeltColor}
              testIdPrefix="dress-shirts-trouser"
            />
          </_Section>

          {/* ── Shoes ────────────────────────────────────────── */}
          <_Section title="Shoes" testid="dress-shirts-shoe-section">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Style</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_STYLES.map(s => (
                    <_Chip
                      key={s.id}
                      selected={shoeStyle === s.id}
                      onClick={() => setShoeStyle(s.id)}
                      label={s.label}
                      testid={`dress-shirts-shoe-style-${s.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Colour</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_COLOURS.map(c => (
                    <_Chip
                      key={c.id}
                      selected={shoeColour === c.id}
                      onClick={() => setShoeColour(c.id)}
                      hex={c.hex}
                      label={c.label}
                      testid={`dress-shirts-shoe-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </_Section>

          {/* ── Submit ───────────────────────────────────────── */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col gap-3">
            {!hasClientImage && (
              <div
                className="px-4 py-3 border border-[#4F2F1F] bg-[#2A1A0F] flex items-start gap-3"
                data-testid="dress-shirts-no-client-photo-warning"
              >
                <div className="text-[#D4A87A] text-sm mt-0.5">⚠</div>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-widest text-[#D4A87A] font-semibold">
                    Client photo required
                  </p>
                  <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
                    The render needs the client&apos;s face reference. Close this modal and upload a photo in the <em>Client Photo</em> panel, then click Render again.
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={generating}
                className="text-[#A1A1AA] hover:bg-[#1A1A1E] rounded-none"
                data-testid="dress-shirts-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={generating || !hasClientImage}
                title={!hasClientImage ? 'Upload a client photo first — the render needs a face reference' : undefined}
                className="rounded-none uppercase tracking-wider text-xs px-6 py-5 disabled:opacity-40"
                style={{ backgroundColor: 'hsl(var(--accent))', color: '#0A0A0C' }}
                data-testid="dress-shirts-submit"
              >
                {generating
                  ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Rendering…</>)
                  : (<><Sparkles className="w-4 h-4 mr-2" /> Render Look</>)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
